import LoadingBar from "@components/ui/LoadingBar";
import Navigation from "@navigation/index";
import firebase from "@services/firebase";
import { useAuthStore } from "@store/auth";
import { useDataStore } from "@store/user_org";
import { doc, onSnapshot } from "firebase/firestore";
import { useEffect, useRef, useState } from "react";
// import Gleap from "gleap";

const isProd = process.env.REACT_APP_ENV === "prod";

type MaintenanceData = {
  active: boolean;
  message: string;
  estimated_end: string;
};

export default function App() {
  const user = useAuthStore((state) => state.user);
  const initAuthListener = useAuthStore((state) => state.initAuthListener);
  const fetchUserData = useDataStore((state) => state.fetchUserData);
  const clearAllData = useDataStore((state) => state.clearAllData);
  const orgData = useDataStore((state) => state.orgData);
  const userData = useDataStore((state) => state.userData);

  const [localVersion, setLocalVersion] = useState<number | null>(null);
  const [showRefreshBanner, setShowRefreshBanner] = useState(false);
  const [maintenanceData, setMaintenanceData] =
    useState<MaintenanceData | null>(null);
  const unsubscribeVersion = useRef<(() => void) | null>(null);

  const isDevOrg = orgData?.id === "OJjnaOZpttbpnWS2Vb6u";
  const isMaintenanceProof = userData?.config?.maintenance_proof === true;

  // // Initialize Gleap.
  // useEffect(() => {
  //   Gleap.initialize("c9NrqEY7tsg0dhTFIiM4Fe25jNP7RmGn");
  // }, []);

  // // Identify user in Gleap
  // useEffect(() => {
  //   if (userData) {
  //     Gleap.identify(userData.id, {
  //       name: userData.infos.full_name || userData.infos.display_name,
  //       email: userData.infos.email,
  //       phone: userData.infos.phone_number,
  //       companyId: userData.org_id,
  //       companyName: orgData?.infos?.name,
  //     });
  //   } else {
  //     Gleap.clearIdentity();
  //   }
  // }, [userData, orgData]);

  // Initialize auth listener on mount
  useEffect(() => {
    const unsubscribe = initAuthListener();
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // Fetch user data when user logs in
  useEffect(() => {
    if (user?.uid) {
      fetchUserData(user.uid);
    } else if (user === null) {
      clearAllData();
    }
  }, [user?.uid]);

  // Fetch local version baked into the build
  useEffect(() => {
    fetch("/version.txt")
      .then((res) => res.text())
      .then((text) => setLocalVersion(parseInt(text.trim(), 10)))
      .catch(() => {});
  }, []);

  // Listen to Firestore version — show banner when a new deployment is available
  useEffect(() => {
    if (localVersion === null) return;

    if (unsubscribeVersion.current) unsubscribeVersion.current();

    const versionRef = doc(firebase.db, "maintenance", "version");
    unsubscribeVersion.current = onSnapshot(versionRef, (snap) => {
      if (!snap.exists()) return;
      const remoteVersion: number = snap.data().react_version;
      if (isProd && remoteVersion > localVersion) {
        setShowRefreshBanner(true);
      }
    });

    return () => {
      if (unsubscribeVersion.current) unsubscribeVersion.current();
    };
  }, [localVersion]);

  // Poll maintenance status via API (public endpoint, no auth needed)
  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const { getMaintenanceStatus } =
          await import("@services/api/maintenance");
        const data = await getMaintenanceStatus();
        setMaintenanceData(data);
      } catch {
        // API not reachable — ignore
      }
    };

    checkMaintenance();
    const interval = setInterval(checkMaintenance, 60_000);
    return () => clearInterval(interval);
  }, [user]);

  const showMaintenance =
    maintenanceData?.active === true && !isDevOrg && !isMaintenanceProof;

  const formatEstimatedEnd = (iso: string) => {
    try {
      const date = new Date(iso);
      return date.toLocaleString("fr-FR", {
        weekday: "long",
        day: "numeric",
        month: "long",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  if (showMaintenance) {
    return (
      <div className="fixed inset-0 z-[9999] bg-neutral-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-8 max-w-md text-center px-6">
          <div className="flex flex-col items-center gap-3">
            <h1 className="text-xl font-semibold text-neutral-900">
              Maintenance en cours
            </h1>
            <p className="text-neutral-500 text-sm leading-relaxed">
              {maintenanceData.message}
            </p>
          </div>

          <div className="w-64">
            <LoadingBar />
          </div>

          {maintenanceData.estimated_end && (
            <p className="text-neutral-400 text-xs">
              Fin estimée : {formatEstimatedEnd(maintenanceData.estimated_end)}
            </p>
          )}

          <button
            onClick={() => useAuthStore.getState().logout()}
            className="mt-4 text-sm text-neutral-400 hover:text-neutral-600 underline transition-colors"
          >
            Se déconnecter
          </button>
        </div>

        <style>{`
          @keyframes maintenance-progress {
            0% { width: 0%; margin-left: 0; }
            50% { width: 60%; margin-left: 20%; }
            100% { width: 0%; margin-left: 100%; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
      <Navigation />
      {showRefreshBanner && isProd && (
        <div
          onClick={() => window.location.reload()}
          className="fixed bottom-10 left-1/2 -translate-x-1/2 z-50 cursor-pointer hover:-translate-y-1 hover:scale-105 transition-all duration-300"
        >
          <div className="px-8 py-4 bg-neutral-900 rounded-xl shadow-lg flex flex-col gap-1 text-white">
            <div className="font-semibold text-h1">
              Une nouvelle version est disponible.
            </div>
            <div className="text-h2 text-neutral-400">
              Cliquez ici pour mettre à jour.
            </div>
          </div>
        </div>
      )}
    </>
  );
}
